// @flow
import * as React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { CallQaQuestion, QA_CATEGORIES, QaCategory, answerColors } from './call-qa-types';

type Props = {
  questions: CallQaQuestion[];
};

// The 30-question audit sheet, grouped into the 3 rubric categories.
// Row layout = Option B (colored left-border) per the design deck.
export const QaAuditSheet = ({ questions }: Props) => (
  <Stack gap={2}>
    {QA_CATEGORIES.map(category => {
      const rows = questions.filter(q => q.category === category);
      if (!rows.length) return null;
      const lost = rows.reduce((sum, q) => sum + (q.deductionApplied || 0), 0);
      const max = rows.reduce((sum, q) => sum + (q.maxDeduction || 0), 0);
      return (
        <Stack key={category} gap={1}>
          <CategoryHeader category={category} lost={lost} max={max} />
          <Stack gap={1}>
            {rows.map(q => <QaRow key={q.id} question={q} />)}
          </Stack>
        </Stack>
      );
    })}
  </Stack>
);

const CategoryHeader = ({ category, lost, max }: { category: QaCategory; lost: number; max: number }) => (
  <Stack data-test={'qa-category-header'} direction={'row'} alignItems={'center'} sx={{ bgcolor: '#F9FAFB', borderRadius: 1, px: 1.5, py: 0.75 }}>
    <Typography variant={'subtitle2'} fontWeight={600} flex={1}>{category}</Typography>
    <Typography variant={'caption'} sx={{ color: lost > 0 ? '#B42318' : 'text.secondary', fontWeight: 600 }}>
      −{lost} pts · {max} max
    </Typography>
  </Stack>
);

const QaRow = ({ question }: { question: CallQaQuestion }) => {
  const c = answerColors(question.answer);
  const lost = question.deductionApplied > 0;
  return (
    <Box data-test={'qa-audit-row'} sx={{
      borderLeft: `3px solid ${c.border}`,
      bgcolor: '#fff',
      borderRadius: '0 6px 6px 0',
      p: 1.5,
      display: 'flex',
      gap: 1.5,
      alignItems: 'flex-start',
    }}>
      <Stack flex={1} gap={0.5}>
        <Typography variant={'body2'}>
          <Box component={'span'} sx={{ color: '#9CA3AF', fontWeight: 600, mr: 0.75 }}>{question.id}</Box>
          {question.question}
        </Typography>
        {question.evidence && (
          <Typography variant={'caption'} color={'text.secondary'} sx={{ fontStyle: 'italic' }}>
            {question.evidence}
          </Typography>
        )}
      </Stack>
      <Stack alignItems={'flex-end'} gap={0.5} sx={{ flexShrink: 0 }}>
        <Chip
          label={question.answer}
          size={'small'}
          sx={{
            bgcolor: c.bg,
            color: c.fg,
            fontWeight: 600,
            fontSize: 11,
            height: 20,
            border: `1px solid ${c.border}`,
          }}
        />
        <Typography variant={'caption'} sx={{ color: lost ? '#B42318' : 'text.secondary', fontWeight: lost ? 600 : 400 }}>
          −{question.deductionApplied} / {question.maxDeduction}
        </Typography>
      </Stack>
    </Box>
  );
};